import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { HttpService } from '../../../../data/service/http.service';
import { ISocialLoginRequest, IResetPasswordRequest, IActivateRequest, ILoginResponse } from '../schema/auth-interfaces';
import { LoginRequest, RegisterRequest } from '../schema/auth-models';
import { Profile } from '../schema/profile';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { CommunityUser } from 'src/app/data/schema/community-user';
import { Community } from 'src/app/data/schema/community';
import { Log } from 'src/app/core/log';


export interface IResetRequest {
  email: string;
  phone: string;
  fromDash: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends HttpService {

  @Output() loginStateChanged: EventEmitter<number> = new EventEmitter();

  isDesktop = false;

  private jwt?: string;
  private userInfo: Profile = new Profile();

  constructor(
    http: HttpClient,
    platform: Platform,
  ) {
    super(http);
    this.endpoint = `${this.apiURL}/auth`;
    this.autoLogin();
    this.isDesktop = !(platform.is('ios') || platform.is('android'));
  }

  get token() { return this.jwt; }
  get profile() { return this.userInfo; }



  autoLogin() {
    const authStr = localStorage.getItem('cbs-session-token') || '{}';
    const authResult = JSON.parse(authStr);
    // Test if we have a token and if the expiration > now
    if (authResult.token && new Date(authResult.tokenExpires) > new Date()) {
      this.storeLoginResult(authResult);
      this.fetchProfile();
      Log.debug('Auto-login successful.');
    } else {
      Log.debug('Auto-login failed. No or Expired token.');
    }
  }

  isLoginValid() {
    const authStr = localStorage.getItem('cbs-session-token') || '{}';
    const authResult = JSON.parse(authStr);

    return (authResult.token && new Date(authResult.tokenExpires) > new Date()) ? true : false;
  }

  logout() {
    this.loginStateChanged.emit(0);
    Log.debug('Logging out..');
    localStorage.removeItem('cbs-session-token');
    localStorage.removeItem('curr_community')
    this.jwt = undefined;
    Community.clearCached();
    CommunityUser.clearCache();
    this.userInfo = new Profile();
    ServiceHelper.menuCtrl.enable(false);
  }

  async login(details: LoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login`);
    } catch (error) {
      console.error('Login error: ' + error);
      throw error;
    }
  }

  async socialLogin(details: ISocialLoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login/${details.provider}`);
    } catch (error) {
      throw error || 'Error logging in.';
    }
  }

  async tokenLogin(cbsToken: string) {
    try {
        return await this.runLogin({ token: cbsToken }, `${this.endpoint}/cbs`);
    } catch (error) {
        console.error('Login error: ' + error);
        throw error;
    }
  }

  async checkIfUserCanBeDeleted() {
    return await this.fetch(`${this.apiURL}/users/canDelete`);
  }

  async register(profile: RegisterRequest) {
    try {
      // Run the register request, posting the data
      profile.fromCBS = true;
      return await this.post(profile, `${this.endpoint}/register`);
    } catch (error) {
      throw error || 'Error registering account.';
    }
  }

  async resendActivation(email: string) {
    try {
      // Run the resend activation, posting the data
      await this.post({ value: email }, `${this.endpoint}/activation/send/false`, 'text');
    } catch (error) {
      throw error || 'Error requesting activation email.';
    }
  }

  async requestReset(request: IResetRequest) {
    try {
      // Run the request reset, posting the data
      await this.post(request, `${this.endpoint}/requestreset`, 'text');
    } catch (error) {
      throw error || 'Error requesting password reset.';
    }
  }

  async resetPassword(request: IResetPasswordRequest) {
    try {
      // Run the reset request, posting the data
      await this.post(request, `${this.endpoint}/passwordreset`, 'text');
    } catch (error) {
      throw error || 'Error resetting password.';
    }
  }

  async activateAccount(request: IActivateRequest) {
    try {
      // Run the activate request, posting the data
      request.source = 'cbs'; 
      return await this.post(request, `${this.endpoint}/activate`, 'text');
    } catch (error) {
      throw error || 'Error activating account.';
    }
  }

  async deleteAccount() {
    const load = await ServiceHelper.displayLoad('Deleting account...');
    try {
      // Run the activate request, posting the data
      await this.delete(`${this.apiURL}/users/${this.profile.userId}`, 'text');
    } catch (error) {
      throw error || 'Error activating account.';
    } finally {
      load.dismiss();
    }
  }


  updateProfile(profile: Profile) {
    this.userInfo = profile;
    // Load the stored result. Replace the profile, and save it again
    const authStr = localStorage.getItem('cbs-session-token') || '{}';
    const authResult = JSON.parse(authStr);
    authResult.profile = profile;
    localStorage.setItem('cbs-session-token', JSON.stringify(authResult));
  }

  protected getUpdateUrl(entity: any): string {
    throw new Error('Method not implemented.');
  }

  protected isUpdate(entity: any): boolean {
    throw new Error('Method not implemented.');
  }

  private async runLogin(data: LoginRequest | ISocialLoginRequest, endpoint: string = this.endpoint) {
    try {
      // Run the login request, posting the data
      const loginResp = await this.post(data, endpoint) as ILoginResponse;
      this.storeLoginResult(loginResp);
    } catch (error) {
      console.error('Error during login', error);
      throw error || 'Error during login.';
    }
  }

  private storeLoginResult(result: ILoginResponse) {
    // set local properties
    this.jwt = result.token;
    this.userInfo = new Profile(result.profile);
    // set the storage
    localStorage.setItem('cbs-session-token', JSON.stringify(result));
    this.loginStateChanged.emit(1);
    // Log.debug('stored login result', result);
    this.updateLoginsRemainingForSurvey();
  }

  private updateLoginsRemainingForSurvey() {
    // Decrement remaining logins (start at 5 if none or restarting)
    let remaining = +(localStorage.getItem(`${this.profile.userId}-survey`) ?? '5');
    remaining = Math.max(remaining - 1, 0);
    localStorage.setItem(`${this.profile.userId}-survey`, remaining.toString());
  }

  async fetchProfile() {
    try {
      const profile = new Profile(await this.fetch(`${this.apiURL}/users/profile`));
      this.updateProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile. Error:', error);
    }
  }

  private parseJWT(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  }



}
